import { forwardRef, ForwardRefRenderFunction, useImperativeHandle, useMemo, useState } from 'react';
import { DataProperty, StackPointWithUuid } from './index';
import { Button, EllipsisSpan, Table } from '@maxtropy/components';
import { TopologicalType } from '@/api/type';
import { BatteryType, PointType } from '@/pages/BsaDataCollectionConfigNew/const';
import { ColumnsType, ColumnType } from 'antd/es/table';
import { parseOnDisplay, parseOnEcho } from '@/components/FormulaInput';
import { getRange } from '@/pages/Bsa/components/Point/modbusPoint/utils';
import { useSearchParams } from 'react-router-dom';
import { Space } from 'antd';
import { cloneDeep, isNil, uniq } from 'lodash-es';
import { DataRow } from './DataTable';
import VirtualDialog from './VirtualDialog';
import { v4 } from 'uuid';
import { ColumnStatusEnum } from '../const';
import ActionStatusTag from '../components/ActionStatusTag';

interface Props {
  stackId: number;
  stackPointList: StackPointWithUuid[];
  setStackPointList: (stackPointList: StackPointWithUuid[]) => void;
  topologicalType: TopologicalType;
  batteryType: BatteryType;
  dataPropertiesAll: DataProperty[];
  hasCreated: boolean;
}

export interface VirtualTableRefProps {
  setEditingRow: (editingRow: DataRow) => void;
  setEditingRowIndex: (editingRowIndex: number) => void;
}

const VirtualTable: ForwardRefRenderFunction<VirtualTableRefProps, Props> = (props, ref) => {
  const { dataPropertiesAll, stackPointList, stackId, setStackPointList, hasCreated } = props;
  const alterMode = false;
  const [searchParams] = useSearchParams();
  const showSingleStep = searchParams.get('showSingleStep') === 'true';

  const dataPropertiesMap = useMemo(
    () => new Map(dataPropertiesAll.map(item => [String(item.id), item.name!])),
    [dataPropertiesAll]
  );

  const [editingRow, setEditingRow] = useState<DataRow | undefined>(undefined);
  const [editingRowIndex, setEditingRowIndex] = useState<number>(-1);

  useImperativeHandle(ref, () => {
    return { setEditingRow, setEditingRowIndex };
  });

  const _editingRow = useMemo(() => {
    if (editingRow) {
      const { formula, valueFloor, valueCeiling, ...reset } = editingRow;
      return { ...reset, formula: parseOnEcho(formula), valueRange: [valueFloor, valueCeiling] };
    } else {
      return editingRow;
    }
  }, [editingRow]);

  const otherColumns: ColumnsType<any> = [
    {
      title: '序号',
      key: 'index',
      width: 70,
      ellipsis: { showTitle: true },
      render: (_, _record, index) => index + 1,
    },
    {
      title: '数据属性/标识符',
      dataIndex: 'name',
      ellipsis: { showTitle: true },
      render: (_: any, record) => (
        <EllipsisSpan value={!!record.hasProperty ? record.dataPropertyName : record.identifier} />
      ),
    },
    {
      title: '单位',
      dataIndex: 'physicalUnitGeneralName',
      ellipsis: { showTitle: true },
      render: (v: string) => <EllipsisSpan value={v} />,
    },
    {
      title: '虚拟点类型',
      dataIndex: 'hasProperty',
      ellipsis: { showTitle: true },
      render: (value: boolean) => <EllipsisSpan value={value ? '建模虚拟点' : '非建模虚拟点'} />,
    },
    {
      title: '是否可写',
      dataIndex: 'writable',
      ellipsis: { showTitle: true },
      render: (v: string) => (
        <EllipsisSpan value={typeof v === 'boolean' ? (v ? '是' : '否') : alterMode ? '点击编辑操作修改' : undefined} />
      ),
    },
    {
      title: '公式',
      dataIndex: 'formula',
      ellipsis: { showTitle: true },
      render: (v: string) => (
        <EllipsisSpan value={v ? parseOnDisplay(v, dataPropertiesMap) : alterMode ? '点击编辑操作修改' : undefined} />
      ),
      onCell: record => {
        if (isNil(record.formula))
          return {
            style: { border: 'solid 1px red' },
          };
        else return {};
      },
    },
    {
      title: '数值合理区间',
      dataIndex: 'valueRange',
      ellipsis: { showTitle: true },
      render: (_: any, record) => {
        const value = getRange(record?.valueFloor, record?.valueCeiling);
        return <EllipsisSpan value={value !== '--' ? value : alterMode ? '点击编辑操作修改' : undefined} />;
      },
    },
  ];

  const actionColumns: ColumnType<any>[] = [
    {
      title: '操作状态',
      width: 80,
      key: 'status',
      fixed: 'right',
      render: (_, record) => <ActionStatusTag columnStatus={record.status}></ActionStatusTag>,
    },
    {
      title: '操作',
      width: 140,
      key: 'action',
      fixed: 'right',
      render: (_, record, index) => (
        <Space>
          <Button
            type="link"
            onClick={() => {
              setEditingRowIndex(index);
              setEditingRow(record);
            }}
          >
            编辑
          </Button>
          <Button
            type="link"
            onClick={() => {
              const newStackPointList = cloneDeep(stackPointList);
              newStackPointList.forEach(stackPoint => {
                if (stackPoint.stackId === stackId && stackPoint.pointType === PointType.VIRTUAL) {
                  stackPoint.templates.splice(index, 1);
                }
              });
              setStackPointList(newStackPointList);
            }}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  const currentStackPointList = stackPointList.filter(item => item.stackId === stackId);
  const data = currentStackPointList.find(item => item.pointType === PointType.VIRTUAL)?.templates || [];
  const columns = [...otherColumns, ...(!hasCreated || showSingleStep ? actionColumns : [])];

  const usedProperties = uniq(
    data
      .filter(item => item.dataPropertyId)
      .filter(item => item.dataPropertyId !== editingRow?.dataPropertyId)
      .map(item => item.dataPropertyId!)
  );
  const usedIdentifier = data
    .filter(item => item.identifier)
    .filter(item => item.identifier !== editingRow?.identifier)
    .map(item => item.identifier!);

  return (
    <>
      <Table rowKey="uuid" columns={columns} dataSource={data}></Table>

      <VirtualDialog
        row={_editingRow}
        onCancel={() => {
          setEditingRowIndex(-1);
          setEditingRow(undefined);
        }}
        onOk={(res, shouldContinue) => {
          const newStackPointList = cloneDeep(stackPointList);
          // rowIndex为-1，是新增
          if (editingRowIndex === -1) {
            newStackPointList.forEach(stackPoint => {
              if (stackPoint.stackId === stackId && stackPoint.pointType === PointType.VIRTUAL) {
                res.uuid = v4();
                res.status = ColumnStatusEnum.ADD;
                stackPoint.templates.push(res);
              }
            });
          }
          // rowIndex不为-1，是编辑
          else {
            newStackPointList.forEach(stackPoint => {
              if (stackPoint.stackId === stackId && stackPoint.pointType === PointType.VIRTUAL) {
                // 没有id，状态设置为新增，否则设置为编辑
                if (!stackPoint.templates[editingRowIndex].id) res.status = ColumnStatusEnum.ADD;
                else res.status = ColumnStatusEnum.EDIT;
                stackPoint.templates[editingRowIndex] = res;
              }
            });
          }
          setStackPointList(newStackPointList);
          // 完成并继续添加
          if (shouldContinue) {
            setEditingRowIndex(-1);
            setEditingRow({ hasProperty: true } as any);
          }
        }}
        dataPropertiesAll={dataPropertiesAll}
        usedProperties={usedProperties}
        usedIdentifier={usedIdentifier}
      ></VirtualDialog>
    </>
  );
};

export default forwardRef(VirtualTable);
